import { useEffect, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useRouteMatch } from 'react-router-dom';

import {
  ModuleTypes,
  TransactionModuleTypes,
} from 'dashboard/apps/DashboardLayoutEditor/constants';
import { ModuleTypeInfo } from 'dashboard/apps/DashboardLayoutEditor/ControlPanel/constants';
import {
  DataDisplayPanel,
  MarkdownPanel,
  ResourceInquiryV2Panel,
} from 'dashboard/apps/DashboardLayoutEditor/ControlPanel/Panels';
import ResourceReportV2Panel from 'dashboard/apps/DashboardLayoutEditor/ControlPanel/Panels/ResourceReportV2Panel';
import ResourceVerificationV2Panel from 'dashboard/apps/DashboardLayoutEditor/ControlPanel/Panels/ResourceVerificationV2Panel';
import LayoutEditor from 'dashboard/apps/DashboardLayoutEditor/LayoutEditor';
import { FormEditTransactionLayoutTab } from 'dashboard/forms/FormEditTransactionLayoutTab/FormEditTransactionLayoutTab';
import { Environment } from 'dashboard/models/environment';
import { Configuration, Tab } from 'dashboard/models/grid-layout';
import { InquiryTemplate } from 'dashboard/models/inquiry-template';
import { Organization } from 'dashboard/models/organization';
import { Template } from 'dashboard/models/template';
import { TransactionType } from 'dashboard/models/transaction-type';
import {
  TransactionModule,
  TransactionTypeGridLayout,
} from 'dashboard/models/transaction-type-grid-layout';
import { VerificationTemplate } from 'dashboard/models/verification-template';
import TransactionModuleBasePreview from 'dashboard/modules/TransactionModuleBase/TransactionModuleBasePreview';
import { TOP_BUTTON_CONTAINER_PORTAL_ID } from 'dashboard/views/DashboardTransactionTypesShowView/DashboardTransactionTypesShowView';
import {
  generateDefaultTransactionModuleConfiguration,
  generateDefaultTransactionTypeGridLayoutConfiguration,
} from 'dashboard/views/DashboardTransactionTypesShowView/helpers';
import Button from 'design-system/components/Button';
import { HStack } from 'design-system/components/Stack';
import { FieldSchema } from 'lib/fields';
import { JsonSchemaObject } from 'lib/json-schema';

const transactionModuleTypes: Record<string, ModuleTypeInfo<TransactionModuleTypes>> = {
  DATA_DISPLAY: {
    type: ModuleTypes.DataDisplay,
    featureFlag: null,
    productFeatures: null,
    internalUsersOnly: false,
  },
  MARKDOWN: {
    type: ModuleTypes.Markdown,
    featureFlag: null,
    productFeatures: null,
    internalUsersOnly: false,
  },
  INQUIRY: {
    type: ModuleTypes.Inquiryv2,
    featureFlag: null,
    productFeatures: null,
    internalUsersOnly: false,
  },
  RELATIONS: {
    type: ModuleTypes.Relations,
    featureFlag: null,
    productFeatures: null,
    internalUsersOnly: false,
  },
  REPORT: {
    type: ModuleTypes.Reportv2,
    featureFlag: null,
    productFeatures: null,
    internalUsersOnly: false,
  },
  VERIFICATION: {
    type: ModuleTypes.Verificationv2,
    featureFlag: null,
    productFeatures: null,
    internalUsersOnly: false,
  },
  TAGS: {
    type: ModuleTypes.Tags,
    featureFlag: null,
    productFeatures: null,
    internalUsersOnly: false,
  },
  EVENT_TIMELINE: {
    type: ModuleTypes.EventTimeline,
    featureFlag: null,
    productFeatures: null,
    internalUsersOnly: false,
  },
  LIST_MATCHES: {
    type: ModuleTypes.ListMatches,
    featureFlag: 'dashboard:decouple-list-matches-from-inquiries',
    productFeatures: null,
    internalUsersOnly: false,
  },
};

export interface TransactionLayoutEditorProps {
  transactionType: TransactionType;
  organization: Organization;
  currentEnvironment: Environment;
  dataSchema: JsonSchemaObject;
  gridLayouts: TransactionTypeGridLayout[];
  fieldSchema: FieldSchema[];
  onTransactionTypeGridLayoutCreate: (configuration: Configuration<TransactionModule>) => void;
  onTransactionTypeGridLayoutSave: (gridLayout: TransactionTypeGridLayout) => void;
  enabledInquiryTemplates: (InquiryTemplate | Template)[];
  publishedVerificationTemplates: VerificationTemplate[];
}

export const TransactionLayoutEditor = (props: TransactionLayoutEditorProps) => {
  const {
    params: { transactionTypeId },
  } = useRouteMatch<{ transactionTypeId: string }>();
  const {
    transactionType,
    organization,
    currentEnvironment,
    dataSchema,
    gridLayouts,
    fieldSchema,
    onTransactionTypeGridLayoutCreate,
    onTransactionTypeGridLayoutSave,
    enabledInquiryTemplates,
    publishedVerificationTemplates,
  } = props;
  const firstGridLayoutConfiguration =
    gridLayouts[0]?.attributes.configuration ??
    generateDefaultTransactionTypeGridLayoutConfiguration();

  const [configuration, setConfiguration] = useState(firstGridLayoutConfiguration);
  useEffect(() => {
    if (gridLayouts[0]?.attributes.configuration) {
      setConfiguration(gridLayouts[0]?.attributes.configuration);
    }
  }, [gridLayouts]);

  const [domReady, setDomReady] = useState(false);
  useLayoutEffect(() => {
    setDomReady(true);
  }, []);

  const handleSaveTransactionLayout = () => {
    const gridLayout = gridLayouts[0];
    if (!gridLayout) {
      onTransactionTypeGridLayoutCreate(configuration);
    } else {
      onTransactionTypeGridLayoutSave({
        ...gridLayout,
        attributes: {
          ...gridLayout.attributes,
          configuration,
        },
      });
    }
  };

  const topButtonContainer = domReady && document.getElementById(TOP_BUTTON_CONTAINER_PORTAL_ID);

  return (
    <>
      {topButtonContainer &&
        createPortal(
          <HStack>
            <Button
              id='transaction-type__settings'
              variant='basic'
              to={`/dashboard/transaction-types/${transactionTypeId}/settings`}
            >
              Settings
            </Button>
            <Button
              id='transaction-type__layout-save'
              variant='primary'
              size='md'
              onClick={handleSaveTransactionLayout}
            >
              Save
            </Button>
          </HStack>,
          topButtonContainer,
        )}
      <LayoutEditor<TransactionModule, Tab<TransactionModule>, TransactionModuleTypes>
        organization={organization}
        currentEnvironment={currentEnvironment}
        layoutConfiguration={configuration}
        dataSchema={dataSchema}
        environmentName={currentEnvironment?.attributes?.name}
        shouldShowVisibilityToggle={true}
        shouldShowVisibilityAdvancedControls={true}
        shouldShowAdvancedOptions={true}
        supportedModuleTypes={transactionModuleTypes}
        supportedComponentTypes={{}}
        generateDefaultModuleConfiguration={generateDefaultTransactionModuleConfiguration}
        updateLayoutConfiguration={(configuration: Configuration<TransactionModule>) => {
          setConfiguration(configuration);
        }}
        data-test='transaction-types-show__layout-editor'
        renderModule={(module) => (
          <TransactionModuleBasePreview
            key={module.token}
            transactionType={transactionType}
            fieldSchema={fieldSchema}
            module={module}
          />
        )}
        renderTabForm={(selectedTab, hideEditModal, handleEdit, handleValidateTabName) => (
          <FormEditTransactionLayoutTab
            tab={selectedTab}
            cancel={hideEditModal}
            submit={handleEdit}
            validateTabName={handleValidateTabName}
          />
        )}
        renderModuleConfiguration={(module, handleUpdateModule) => {
          switch (module.type) {
            case ModuleTypes.Markdown:
              return (
                <MarkdownPanel
                  key={module.token}
                  module={module}
                  updateModule={handleUpdateModule}
                />
              );
            case ModuleTypes.DataDisplay:
              return (
                <DataDisplayPanel
                  key={module.token}
                  module={module}
                  dataSchema={dataSchema}
                  updateModule={handleUpdateModule}
                />
              );
            case ModuleTypes.Resource:
              switch (module['field-key']) {
                case 'inquiries':
                  return (
                    <ResourceInquiryV2Panel
                      enabledInquiryTemplates={enabledInquiryTemplates}
                      dataSchema={dataSchema}
                      key={module.token}
                      module={module}
                      updateModule={handleUpdateModule}
                    />
                  );
                case 'reports':
                  return (
                    <ResourceReportV2Panel
                      dataSchema={dataSchema}
                      key={module.token}
                      module={module}
                      updateModule={handleUpdateModule}
                    />
                  );
                case 'verifications':
                  return (
                    <ResourceVerificationV2Panel
                      dataSchema={dataSchema}
                      key={module.token}
                      module={module}
                      publishedVerificationTemplates={publishedVerificationTemplates}
                      updateModule={handleUpdateModule}
                    />
                  );
              }
              break;
            default: {
              return null;
            }
          }
        }}
      />
    </>
  );
};
